'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { authClient } from '@/lib/auth/client';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';

function createLoginSchema(t: (key: string) => string) {
  return z.object({
    email: z
      .string()
      .min(1, t('validation.emailRequired'))
      .email(t('validation.invalidEmail')),
    password: z.string().min(1, t('validation.passwordRequired')),
    rememberMe: z.boolean().default(false),
  });
}

type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

const entrance = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0 },
};

export function LoginForm() {
  const router = useRouter();
  const t = useTranslations('auth.signIn');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const loginSchema = createLoginSchema(t);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  async function onSubmit(data: LoginFormData) {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      // Use destructuring pattern from better-auth docs
      const { data: result, error } = await authClient.signIn.email({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
      });

      if (error) {
        const code = error.code;
        if (code === 'INVALID_EMAIL_OR_PASSWORD') {
          setErrorMessage(t('errors.invalidCredentials'));
        } else if (code === 'USER_BANNED') {
          setErrorMessage(t('errors.accountDeactivated'));
        } else if (code === 'TOO_MANY_ATTEMPTS') {
          setErrorMessage(t('errors.tooManyAttempts'));
        } else {
          setErrorMessage(t('errors.unableToSignIn'));
        }
        return;
      }

      router.push('/dashboard');
      router.refresh();
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('errors.unableToSignIn'));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <motion.div
      initial="hidden"
      animate="show"
      transition={{ staggerChildren: 0.06 }}
      className="space-y-8"
    >
      {/* Accent + Heading */}
      <motion.div variants={entrance} className="space-y-3">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
          <div className="w-6 h-[2px] bg-primary/30 rounded-full" />
        </div>
        <h2 className="text-3xl font-display font-bold text-foreground tracking-tight">
          {t('welcomeBack')}
        </h2>
        <p className="text-[15px] text-muted-foreground">
          {t('accountSubtitle')}
        </p>
      </motion.div>

      {/* Error */}
      {errorMessage && (
        <motion.div
          variants={entrance}
          initial="hidden"
          animate="show"
          key={errorMessage}
        >
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        </motion.div>
      )}

      {/* Form */}
      <motion.div variants={entrance}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    {t('emailLabel')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('emailInputPlaceholder')}
                      autoComplete="email"
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className="text-sm font-medium">
                      {t('passwordLabel')}
                    </FormLabel>
                    <Link
                      href="/reset-password"
                      className="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                    >
                      {t('forgotPassword')}
                    </Link>
                  </div>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t('passwordPlaceholder')}
                      autoComplete="current-password"
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2.5 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="text-sm font-normal text-muted-foreground cursor-pointer">
                    {t('rememberMe30Days')}
                  </FormLabel>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full h-11 text-sm font-semibold tracking-wide"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('signingIn')}
                </>
              ) : (
                <>
                  {t('signInButton')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </Form>
      </motion.div>

      {/* Footer link */}
      <motion.p
        variants={entrance}
        className="text-center text-sm text-muted-foreground"
      >
        {t('noAccount')}{' '}
        <Link
          href="/register"
          className="text-primary font-medium hover:text-primary/80 transition-colors"
        >
          {t('createAccountLink')}
        </Link>
      </motion.p>
    </motion.div>
  );
}
